import React, { useState, useEffect } from "react";
import "./FilterSelector.scss";

const FilterSelector = ({ 
  filterType = "text", 
  placeholder = "Enter value...", 
  onApply, 
  currentValue = "",
  options = [] // For select type filters
}) => {
  const isRange = filterType === "numberRange";
  const toRange = (value) => ({ min: value?.min ?? "", max: value?.max ?? "" });
  const [inputValue, setInputValue] = useState(isRange ? "" : currentValue);
  const [rangeValue, setRangeValue] = useState(toRange(currentValue));

  useEffect(() => {
    if (isRange) {
      setRangeValue(toRange(currentValue));
    } else {
      setInputValue(currentValue);
    }
  }, [currentValue, isRange]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleRangeChange = (bound) => (e) => {
    setRangeValue({ ...rangeValue, [bound]: e.target.value });
  };

  const hasRangeValue = String(rangeValue.min).trim() !== "" || String(rangeValue.max).trim() !== "";

  const isRangeInvalid = () => {
    const { min, max } = rangeValue;
    return String(min).trim() !== "" && String(max).trim() !== "" && Number(min) > Number(max);
  };

  const handleApply = () => {
    if (isRange) {
      const min = String(rangeValue.min).trim();
      const max = String(rangeValue.max).trim();
      onApply({
        value: min || max ? { min, max } : "",
        label: min || max ? `${min || "…"} - ${max || "…"}` : "All"
      });
      return;
    }

    onApply({
      value: inputValue.trim(),
      label: inputValue.trim() || "All"
    });
  };

  const handleClear = () => {
    setInputValue("");
    setRangeValue({ min: "", max: "" });
    onApply({
      value: "",
      label: "All"
    });
  };

  const handleKeyPress = (e) => {
    if (e.key !== 'Enter') {
      return;
    }
    if (isRange && isApplyDisabled()) {
      return;
    }
    handleApply();
  };

  const isApplyDisabled = () => {
    if (isRange) {
      const current = toRange(currentValue);
      const unchanged = String(rangeValue.min).trim() === String(current.min) && String(rangeValue.max).trim() === String(current.max);
      return unchanged || isRangeInvalid();
    }
    return inputValue.trim() === currentValue.trim();
  };

  const renderFilterInput = () => {
    switch (filterType) {
      case "select":
        return (
          <select
            value={inputValue}
            onChange={handleInputChange}
            className="filter-select-input"
          >
            <option value="">All</option>
            {options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "date":
        return (
          <input
            type="date"
            value={inputValue}
            onChange={handleInputChange}
            className="filter-date-input"
            max={new Date().toISOString().split('T')[0]} // Prevent future dates
          />
        );

      case "number":
        return (
          <input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="filter-number-input"
            min="0"
          />
        );

      case "numberRange":
        return (
          <div className="filter-range-inputs">
            <input
              type="number"
              value={rangeValue.min}
              onChange={handleRangeChange("min")}
              onKeyPress={handleKeyPress}
              placeholder="Min"
              className="filter-number-input"
              min="0"
            />
            <input
              type="number"
              value={rangeValue.max}
              onChange={handleRangeChange("max")}
              onKeyPress={handleKeyPress}
              placeholder="Max"
              className="filter-number-input"
              min="0"
            />
          </div>
        );

      case "text":
      default:
        return (
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="filter-text-input"
          />
        );
    }
  };

  return (
    <div className="filter-selector-container">
      <div className="filter-input-section">
        <label className="filter-label">
          {filterType === "text" && "Search by text:"}
          {filterType === "select" && "Filter by option:"}
          {filterType === "date" && "Filter by date:"}
          {filterType === "number" && "Filter by number:"}
          {filterType === "numberRange" && "Filter by range:"}
        </label>
        
        {renderFilterInput()}
      </div>

      <div className="filter-actions">
        <button 
          className="clear-button"
          onClick={handleClear}
          disabled={isRange ? !hasRangeValue : !inputValue.trim()}
        >
          CLEAR
        </button>
        
        <button 
          className={`apply-button ${isApplyDisabled() ? 'disabled' : ''}`}
          onClick={handleApply}
          disabled={isApplyDisabled()}
        >
          APPLY
        </button>
      </div>
    </div>
  );
};

export default FilterSelector;
